import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Clock3, Star } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageLoadingSkeleton } from '../components/PageLoadingSkeleton';
import { getUserFacingError } from '../lib/errors';
import { formatCurrency } from '../lib/formatters';

const initialForm = {
  name: '',
  email: '',
  company: '',
  brief: '',
};

const pendingOrderKey = 'agentichire.pendingOrder';

function readPendingOrder() {
  try {
    const raw = window.localStorage.getItem(pendingOrderKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePendingOrder(payload) {
  window.localStorage.setItem(
    pendingOrderKey,
    JSON.stringify({
      payload,
      savedAt: new Date().toISOString(),
    })
  );
}

function clearPendingOrder() {
  window.localStorage.removeItem(pendingOrderKey);
}

function formatBillingInterval(interval) {
  switch (interval) {
    case 'week':
      return 'wk';
    case 'quarter':
      return 'quarter';
    case 'year':
      return 'yr';
    case 'month':
    default:
      return 'mo';
  }
}

function hasItems(items) {
  return Array.isArray(items) && items.length > 0;
}

function getItems(items) {
  return Array.isArray(items) ? items : [];
}

function formatFieldLabel(value) {
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatIncludedUsageLabel(key, value) {
  const label = formatFieldLabel(key);
  return `${value} ${Number(value) === 1 ? label.replace(/s$/, '') : label}`;
}

function formatBoolean(value) {
  return value ? 'Yes' : 'No';
}

export function ListingPage({ snapshot, loading, currentUser, onCreateOrder }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('');
  const [processingPendingOrder, setProcessingPendingOrder] = useState(false);

  const listing = useMemo(() => {
    if (!snapshot) {
      return null;
    }

    return snapshot.listings.find((entry) => entry.slug === slug) || null;
  }, [slug, snapshot]);

  const startCheckout = useCallback(async (payload) => {
    const result = await onCreateOrder(payload);

    if (result?.checkoutUrl) {
      window.location.href = result.checkoutUrl;
      return;
    }

    if (result?.order?.id) {
      navigate(`/orders/${result.order.id}`);
      return;
    }

    setStatus(result.mock ? 'Saved in sample mode.' : 'Order created.');
  }, [navigate, onCreateOrder]);

  useEffect(() => {
    let active = true;

    async function processPendingOrder() {
      if (!currentUser || !listing || !onCreateOrder || processingPendingOrder) {
        return;
      }

      const pendingOrder = readPendingOrder();
      if (!pendingOrder?.payload || pendingOrder.payload.listingId !== listing.id) {
        return;
      }

      setProcessingPendingOrder(true);
      setStatus('Starting your order...');

      try {
        await startCheckout(pendingOrder.payload);
        clearPendingOrder();
      } catch (error) {
        if (active) {
          setStatus(getUserFacingError(error, 'Unable to start that order right now. Please try again.'));
        }
      } finally {
        if (active) {
          setProcessingPendingOrder(false);
        }
      }
    }

    processPendingOrder();

    return () => {
      active = false;
    };
  }, [currentUser, listing, onCreateOrder, processingPendingOrder, startCheckout]);

  if (loading || !snapshot) {
    return <PageLoadingSkeleton label="Loading listing" />;
  }

  if (!listing) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Listing not found</h1>
          <p>This agent either does not exist yet or has not been published.</p>
        </div>
      </section>
    );
  }

  const priceDisplayLine =
    listing.pricingType === 'subscription'
      ? `${formatCurrency(listing.priceFrom)} /${formatBillingInterval(listing.billingInterval)}`
      : formatCurrency(listing.priceFrom);
  const deliveryPricingLine =
    listing.pricingType === 'subscription' ? priceDisplayLine : `${formatCurrency(listing.priceFrom)} one time`;
  const hasUploadedCover = Boolean(listing.thumbnailUrl);
  const isOwnListing = Boolean(currentUser?.id && listing.seller?.id === currentUser.id);
  const buyerInputs = listing.buyerInputs || {};
  const dataPolicy = listing.dataPolicy || {};
  const deliverables = getItems(listing.deliverables);
  const workflow = getItems(listing.workflow);
  const useCases = getItems(listing.useCases);
  const outcomes = getItems(listing.outcomes);
  const stack = getItems(listing.stack);
  const toolsUsed = getItems(listing.toolsUsed);
  const integrations = getItems(listing.integrations);
  const industries = getItems(listing.industries);
  const bestFor = getItems(listing.bestFor);
  const notFor = getItems(listing.notFor);
  const outputFormats = getItems(listing.outputFormats);
  const limitations = getItems(listing.limitations);
  const prohibitedUses = getItems(listing.prohibitedUses);
  const reviews = getItems(listing.reviews);
  const faqs = getItems(listing.faqs);
  const includedUsageEntries = Object.entries(listing.includedUsage || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
  const buyerInputItems = [
    ...getItems(buyerInputs.required).map((item) => ({ ...item, groupLabel: 'Required' })),
    ...getItems(buyerInputs.optional).map((item) => ({ ...item, groupLabel: 'Optional' })),
  ];
  const coverageItems = hasItems(useCases) ? useCases : deliverables.map((item) => item.name).filter(Boolean);
  const hasCoveragePanel =
    hasItems(coverageItems) || hasItems(buyerInputItems) || includedUsageEntries.length > 0;
  const hasDeliverablesPanel =
    hasItems(deliverables) ||
    hasItems(workflow) ||
    Boolean(listing.sampleOutput?.enabled && listing.sampleOutput?.url);
  const hasMarketPanel =
    hasItems(outcomes) ||
    hasItems(stack) ||
    hasItems(toolsUsed) ||
    hasItems(integrations) ||
    hasItems(industries) ||
    hasItems(bestFor) ||
    hasItems(notFor) ||
    hasItems(outputFormats);
  const hasTrustPanel =
    Boolean(listing.featuredQuote) ||
    hasItems(limitations) ||
    hasItems(prohibitedUses) ||
    Boolean(listing.dataPolicy);
  const listingHeroDetails = (
    <div className="listing-hero-overlay">
      <span className="eyebrow left">{listing.category?.name}</span>
      <h1>{listing.title}</h1>
      <p>{listing.description}</p>

      <div className="listing-hero-metrics">
        <span className="listing-hero-rating">
          <Star size={14} />
          {listing.rating.toFixed(1)} rating
        </span>
        <span className="listing-hero-response">
          <Clock3 size={14} />
          {listing.responseHours}h avg response
        </span>
        <span className="listing-hero-verified">
          <CheckCircle2 size={14} />
          Verified
        </span>
      </div>
    </div>
  );

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      if (!currentUser) {
        writePendingOrder({
          listingId: listing.id,
          listingSlug: listing.slug,
          ...form,
        });
        navigate(`/login?mode=signup&redirect=/agents/${encodeURIComponent(listing.slug)}`);
        return;
      }

      setStatus('Starting checkout...');
      await startCheckout({
        listingId: listing.id,
        brief: form.brief,
      });
      setForm(initialForm);
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to start checkout right now. Please try again.'));
    }
  }

  return (
    <section className="page-shell listing-page">
      <div className="listing-breadcrumbs">
        <Link
          to={
            listing.category?.slug
              ? `/explore?categories=${encodeURIComponent(listing.category.slug)}`
              : '/explore'
          }
          className="breadcrumb-link"
        >
          {listing.category?.name || 'Explore'}
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current" aria-current="page">
          {listing.title}
        </span>
      </div>

      <div className="detail-grid marketplace-detail-grid">
        <div className="detail-main">
          <section className="listing-showcase">
            <div
              className={`listing-hero-panel${hasUploadedCover ? ' has-image' : ''} tone-${
                listing.tone || 'graphite'
              }`}
            >
              {hasUploadedCover ? (
                <img className="listing-hero-image" src={listing.thumbnailUrl} alt="" />
              ) : null}
              {hasUploadedCover ? null : listingHeroDetails}
            </div>

            {hasUploadedCover ? (
              <div className="detail-panel listing-hero-detail-panel">{listingHeroDetails}</div>
            ) : null}

            <div className="detail-panel">
              <div className="seller-profile-row">
                <div className="seller-avatar large">{listing.seller?.name?.slice(0, 1) || 'A'}</div>
                <div>
                  <strong>{listing.seller?.name}</strong>
                  <p>{listing.seller?.headline}</p>
                </div>
              </div>
            </div>
          </section>

          {hasCoveragePanel ? (
            <section className="detail-panel">
              <h2>What this service covers</h2>
              <div className="detail-columns">
                <div>
                  <h3>Scope</h3>
                  {hasItems(coverageItems) ? (
                    <div className="pill-row">
                      {coverageItems.map((item) => (
                        <span key={item} className="outline-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>{listing.summary}</p>
                  )}
                </div>

                {includedUsageEntries.length > 0 ? (
                  <div>
                    <h3>Included usage</h3>
                    <ul className="feature-list">
                      {includedUsageEntries.map(([key, value]) => (
                        <li key={key}>{formatIncludedUsageLabel(key, value)}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              {hasItems(buyerInputItems) ? (
                <div className="listing-subsection">
                  <h3>Buyer inputs</h3>
                  <div className="listing-detail-list">
                    {buyerInputItems.map((item) => (
                      <div key={`${item.groupLabel}-${item.name}`} className="listing-detail-item">
                        <strong>
                          {item.name} <span>{item.groupLabel}</span>
                        </strong>
                        {item.description ? <p>{item.description}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          <section className="detail-panel">
            <h2>Pricing and delivery</h2>
            <div className="detail-columns">
              <div>
                <h3>Pricing terms</h3>
                <strong className="listing-pricing-label">{listing.pricingLabel || priceDisplayLine}</strong>
                <p>{listing.pricingDescription || listing.summary}</p>
              </div>
              <div>
                <h3>Delivery terms</h3>
                <ul className="feature-list">
                  <li>{deliveryPricingLine}</li>
                  <li>{listing.deliveryDays} day delivery</li>
                  <li>{listing.revisionCount} revisions included</li>
                  <li>{listing.responseHours}h average response time</li>
                </ul>
              </div>
            </div>
          </section>

          {hasDeliverablesPanel ? (
            <section className="detail-panel">
              <h2>Deliverables and workflow</h2>
              <div className="detail-columns">
                {hasItems(deliverables) ? (
                  <div>
                    <h3>Deliverables</h3>
                    <div className="listing-detail-list">
                      {deliverables.map((item) => (
                        <div key={item.name} className="listing-detail-item">
                          <strong>{item.name}</strong>
                          {item.description ? <p>{item.description}</p> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {hasItems(workflow) ? (
                  <div>
                    <h3>Workflow</h3>
                    <ol className="feature-list">
                      {workflow.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : null}
              </div>

              {listing.sampleOutput?.enabled && listing.sampleOutput?.url ? (
                <div className="sample-output-callout">
                  <div>
                    <h3>Sample output</h3>
                    <p>{listing.sampleOutput.description || 'Review an example of what this service can deliver.'}</p>
                  </div>
                  <a
                    className="ghost-button ui-button"
                    href={listing.sampleOutput.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View sample
                  </a>
                </div>
              ) : null}
            </section>
          ) : null}

          {hasMarketPanel ? (
            <section className="detail-panel">
              <h2>Fit and capabilities</h2>
              <div className="detail-columns">
                {hasItems(outcomes) ? (
                  <div>
                    <h3>Expected outcomes</h3>
                    <ul className="feature-list">
                      {outcomes.map((outcome) => (
                        <li key={outcome}>{outcome}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {hasItems(stack) || hasItems(toolsUsed) || hasItems(integrations) ? (
                  <div>
                    <h3>Tools and integrations</h3>
                    <div className="pill-row">
                      {[...stack, ...toolsUsed, ...integrations].map((item) => (
                        <span key={item} className="outline-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {hasItems(bestFor) || hasItems(industries) ? (
                  <div>
                    <h3>Best for</h3>
                    <div className="pill-row">
                      {[...bestFor, ...industries].map((item) => (
                        <span key={item} className="outline-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {hasItems(outputFormats) ? (
                  <div>
                    <h3>Output formats</h3>
                    <div className="pill-row">
                      {outputFormats.map((item) => (
                        <span key={item} className="outline-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {hasTrustPanel ? (
            <section className="detail-panel">
              <h2>Trust and policies</h2>
              {listing.featuredQuote ? (
                <div className="featured-quote">
                  <h3>Featured quote</h3>
                  <p>"{listing.featuredQuote}"</p>
                </div>
              ) : null}

              <div className="detail-columns">
                {hasItems(limitations) ? (
                  <div>
                    <h3>Limitations</h3>
                    <ul className="feature-list">
                      {limitations.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {hasItems(prohibitedUses) ? (
                  <div>
                    <h3>Prohibited uses</h3>
                    <div className="pill-row">
                      {prohibitedUses.map((item) => (
                        <span key={item} className="outline-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {listing.dataPolicy ? (
                  <div>
                    <h3>Data policy</h3>
                    <ul className="feature-list">
                      <li>Stores buyer data: {formatBoolean(dataPolicy.storesBuyerData)}</li>
                      <li>Uses buyer data for training: {formatBoolean(dataPolicy.usesBuyerDataForTraining)}</li>
                      <li>Requires credentials: {formatBoolean(dataPolicy.requiresCredentials)}</li>
                      <li>Uses third-party tools: {formatBoolean(dataPolicy.thirdPartyToolsUsed)}</li>
                      <li>Requires buyer review: {formatBoolean(listing.requiresBuyerReview)}</li>
                    </ul>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {reviews.length > 0 ? (
            <section className="detail-panel">
              <h2>Reviews</h2>
              <div className="review-list">
                {reviews.map((review) => (
                  <article key={review.id} className={`review-card tone-${listing.tone || 'graphite'}`}>
                    <p>"{review.body}"</p>
                    <div className="review-person">
                      <div className="seller-avatar review-avatar">{review.reviewerName?.slice(0, 1) || 'R'}</div>
                      <div className="review-person-copy">
                        <strong>{review.reviewerName}</strong>
                        {review.company ? <span>{review.company}</span> : null}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {faqs.length > 0 ? (
            <section className="detail-panel">
              <h2>FAQ</h2>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <details key={faq.question} className="faq-item" open={index === 0}>
                    <summary>
                      <strong>{faq.question}</strong>
                      <ChevronDown size={20} />
                    </summary>
                    <p className="faq-answer">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="detail-sidebar">
          <div className="detail-panel sticky-panel">
            <div className="sticky-price-row">
              <div>
                <span className="metric-label">Pricing</span>
                <strong>{priceDisplayLine}</strong>
              </div>
              <div className="sticky-rating">
                <Star size={14} />
                {listing.rating.toFixed(1)}
              </div>
            </div>

            <div className="sidebar-bullets">
              <span>{listing.deliveryDays} day delivery</span>
              <span>{listing.revisionCount} revisions</span>
              <span>{listing.ordersCompleted} orders completed</span>
            </div>

            {isOwnListing ? (
              <div className="owner-listing-actions">
                <p className="status-note">You are viewing your own listing.</p>
                <Link to={`/studio/edit/${listing.id}`} className="primary-button ui-button full-width">
                  Edit Listing
                </Link>
              </div>
            ) : (
              <form className="inquiry-form ui-form" onSubmit={handleSubmit}>
                {!currentUser ? (
                  <>
                    <input
                      className="ui-input"
                      type="text"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                      required
                    />
                    <input
                      className="ui-input"
                      type="email"
                      placeholder="Work email"
                      value={form.email}
                      onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                      required
                    />
                    <input
                      className="ui-input"
                      type="text"
                      placeholder="Company"
                      value={form.company}
                      onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))}
                    />
                  </>
                ) : null}
                <textarea
                  className="ui-textarea"
                  rows="5"
                  placeholder="Describe the workflow you want this agent to handle"
                  value={form.brief}
                  onChange={(event) => setForm((current) => ({ ...current, brief: event.target.value }))}
                  required
                />
                <button type="submit" className="primary-button ui-button full-width">
                  Start order
                </button>
                {status ? <p className="status-note">{status}</p> : null}
              </form>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
